import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { Code, EmptyState, GlassIconButton } from 'dashboard/components/common';

/**
 * Edit a single mail template. Shows a "Custom" / "Platform default"
 * chip and offers a revert button when the brand has overridden.
 */
export default function TemplateEditor({ brandId, templateKey, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [form, setForm] = useState({ subject: '', html_body: '', text_body: '', variables: [] });
  const [platform, setPlatform] = useState(null);
  const [hasBrandOverride, setHasBrandOverride] = useState(false);

  useEffect(() => {
    setLoading(true);
    API.getBrandMailTemplate({ brand_id: brandId, key: templateKey })
      .then(({ brand, platform: plat }) => {
        setPlatform(plat);
        if (brand) {
          setHasBrandOverride(true);
          setForm({
            subject: brand.subject || '',
            html_body: brand.html_body || '',
            text_body: brand.text_body || '',
            variables: brand.variables || plat?.variables || [],
          });
        } else if (plat) {
          setHasBrandOverride(false);
          setForm({
            subject: plat.subject || '',
            html_body: plat.html_body || '',
            text_body: plat.text_body || '',
            variables: plat.variables || [],
          });
        }
      })
      .catch((err) => toast.error(err?.message || 'Failed to load template'))
      .finally(() => setLoading(false));
  }, [brandId, templateKey]);

  const save = () => {
    setSaving(true);
    API.upsertBrandMailTemplate({
      brand_id: brandId,
      key: templateKey,
      patch: form,
    })
      .then(() => { toast.success('Template saved'); onSaved && onSaved(); })
      .catch((err) => toast.error(err?.message || 'Failed to save'))
      .finally(() => setSaving(false));
  };

  const revert = () => {
    if (!hasBrandOverride) return;
    if (!window.confirm('Revert to the platform default for this template?')) return;
    API.deleteBrandMailTemplate({ brand_id: brandId, key: templateKey })
      .then(() => {
        toast.success('Reverted to platform default');
        setHasBrandOverride(false);
        if (platform) {
          setForm({
            subject: platform.subject || '',
            html_body: platform.html_body || '',
            text_body: platform.text_body || '',
            variables: platform.variables || [],
          });
        }
        onSaved && onSaved();
      })
      .catch((err) => toast.error(err?.message || 'Failed to revert'));
  };

  const sendTest = () => {
    if (!testTo) return;
    setTesting(true);
    const vars = {};
    (form.variables || []).forEach((v) => { vars[v] = `[${v}]`; });
    API.testSendBrandMail({ brand_id: brandId, key: templateKey, to: testTo, vars })
      .then((res) => {
        if (res?.sent) toast.success(`Test email sent to ${testTo}`);
        else toast.warning(res?.reason || 'Mail not sent');
      })
      .catch((err) => toast.error(err?.message || 'Failed to send test'))
      .finally(() => setTesting(false));
  };

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        <Stack direction='row' alignItems='center' gap='10px'>
          <Typography>{templateKey}</Typography>
          <Chip
            size='small'
            color={hasBrandOverride ? 'secondary' : 'default'}
            label={hasBrandOverride ? 'Custom' : 'Platform default'}
          />
        </Stack>
        <GlassIconButton label='Close' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size='small'>
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <EmptyState>Loading…</EmptyState>
        ) : (
          <Stack spacing='14px'>
            <TextField
              size='small'
              label='Subject'
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              fullWidth
            />
            <TextField
              size='small'
              label='HTML body'
              multiline
              minRows={10}
              value={form.html_body}
              onChange={(e) => setForm({ ...form, html_body: e.target.value })}
              fullWidth
              sx={{ '& textarea': { fontFamily: 'monospace' } }}
            />
            <TextField
              size='small'
              label='Plain-text fallback (optional)'
              multiline
              minRows={4}
              value={form.text_body}
              onChange={(e) => setForm({ ...form, text_body: e.target.value })}
              fullWidth
              sx={{ '& textarea': { fontFamily: 'monospace' } }}
            />
            {form.variables?.length > 0 && (
              <Box>
                <Typography variant='body2' fontWeight='600' mb='4px'>Available variables</Typography>
                <Stack direction='row' flexWrap='wrap' gap='6px'>
                  {form.variables.map((v) => (
                    <Code key={v}>{`{{${v}}}`}</Code>
                  ))}
                </Stack>
              </Box>
            )}
            <Divider />
            <Box>
              <Typography variant='body2' fontWeight='600' mb='6px'>Send a test</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing='8px'>
                <TextField
                  size='small'
                  placeholder='you@yourbrand.com'
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  fullWidth
                />
                <Button onClick={sendTest} disabled={!testTo || testing}>
                  {testing ? 'Sending…' : 'Send test'}
                </Button>
              </Stack>
              <Typography variant='caption' color='text.secondary' mt='6px' display='block'>
                Variables are replaced with <Code>[var_name]</Code> placeholders for tests.
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        {hasBrandOverride && (
          <Button variant='outlined' color='error' onClick={revert}>
            Revert to default
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={save} disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
